import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Form, Button, Card, Spinner, Alert } from 'react-bootstrap'
import { usePriceExtractionsFetch } from '../../../hooks/fetch/usePriceExtractionsFetch'
import type { ProjectOption } from '../../../interfaces/priceExtraction'
import SpecialSelect, { OptionSelectType } from '../../../components/ui/SpecialSelect'

// Países disponibles (mismos que en el Master Sheet)
const COUNTRIES = [
    { id: 1, name: 'Uruguay', code: 'UY' },
    { id: 2, name: 'Paraguay', code: 'PY' },
    { id: 3, name: 'Bolivia', code: 'BO' },
    { id: 4, name: 'España', code: 'ES' },
    { id: 5, name: 'Colombia', code: 'CO' },
    { id: 6, name: 'Panamá', code: 'PA' },
    { id: 7, name: 'Chile', code: 'CL' },
    { id: 8, name: 'República Dominicana', code: 'DO' },
    { id: 9, name: 'Costa Rica', code: 'CR' }
]

// Uruguay por defecto
const DEFAULT_COUNTRY: OptionSelectType = { value: 1, label: 'Uruguay' }

export const RunFromSheetForm = () => {
    const navigate = useNavigate()
    const { getProjectsByCountry, runFromSheet, showAlertMessage } = usePriceExtractionsFetch()

    const [projects, setProjects] = useState<ProjectOption[]>([])
    const [loadingProjects, setLoadingProjects] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [initialized, setInitialized] = useState(false)

    // Form state
    const [selectedCountry, setSelectedCountry] = useState<OptionSelectType>(DEFAULT_COUNTRY)
    const [selectedProject, setSelectedProject] = useState<OptionSelectType | null>(null)

    // Result state
    const [result, setResult] = useState<{
        success: boolean
        extractionId?: number
        projectName?: string
        changesPending?: number
        totalUnits?: number
        country?: string
        error?: string
    } | null>(null)

    // Validation
    const [hasError, setHasError] = useState(false)

    // Si la request se demora mas de 80s (antes del timeout de Cloudflare ~100s),
    // mostramos un boton de escape al listado de extracciones.
    const [showEscapeButton, setShowEscapeButton] = useState(false)

    useEffect(() => {
        if (!submitting) {
            setShowEscapeButton(false)
            return
        }
        const t = setTimeout(() => setShowEscapeButton(true), 80_000)
        return () => clearTimeout(t)
    }, [submitting])

    // Cargar proyectos por país
    const loadProjects = async (countryId: number) => {
        setLoadingProjects(true)
        setSelectedProject(null)
        const response = await getProjectsByCountry(countryId)
        setLoadingProjects(false)
        if (response.success) {
            setProjects(response.data)
        }
    }

    // Inicializar datos una sola vez
    if (!initialized) {
        setInitialized(true)
        loadProjects(DEFAULT_COUNTRY.value as number)
    }

    const handleCountryChange = (option: OptionSelectType) => {
        setSelectedCountry(option)
        setSelectedProject(null)
        setResult(null)
        loadProjects(option.value as number)
    }

    const handleProjectChange = (option: OptionSelectType) => {
        setSelectedProject(option)
        setHasError(false)
        setResult(null)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!selectedProject?.value) {
            setHasError(true)
            showAlertMessage('danger', 'Seleccione un proyecto')
            return
        }

        setSubmitting(true)
        setResult(null)

        const response = await runFromSheet(selectedProject.value as number)

        setSubmitting(false)

        if (response.success && response.data) {
            setResult({
                success: true,
                extractionId: response.data.extractionId,
                projectName: response.data.projectName,
                changesPending: response.data.changesPending,
                totalUnits: response.data.totalUnits,
                country: (response.data as any).country
            })
            showAlertMessage('success', `Extraccion completada. ${response.data.changesPending} cambios detectados.`)
        } else {
            setResult({
                success: false,
                error: response.message || 'Error al ejecutar la extraccion'
            })
            showAlertMessage('danger', response.message || 'Error al ejecutar la extraccion')
        }
    }

    const countryOptions: OptionSelectType[] = COUNTRIES.map(c => ({
        value: c.id,
        label: `${c.name} (${c.code})`
    }))

    const projectOptions: OptionSelectType[] = projects.map(p => ({
        value: p.id,
        label: `${p.id} - ${p.title}`
    }))

    return (
        <div className="p-5">
            <div className="mb-4 py-2 px-4 d-flex justify-content-between flex-wrap position-relative">
                <Link to="/admin/price-extractions" className="fs-2 go-back mt-3 ms-3 ms-md-4">
                    <i className="text-light bi bi-arrow-left"></i>
                </Link>
                <h3>EJECUTAR EXTRACCION DESDE MASTER SHEET</h3>
                <div style={{ width: 150 }}></div>
            </div>

            <Card className="mx-auto" style={{ maxWidth: 600 }}>
                <Card.Body className="p-4">
                    <Alert variant="info" className="mb-4">
                        <i className="bi bi-info-circle me-2"></i>
                        Esta opcion ejecuta la extraccion usando los archivos configurados en el Master Sheet (Link1 y Link2) para el proyecto seleccionado.
                    </Alert>

                    <Form onSubmit={handleSubmit}>
                        {/* País */}
                        <Form.Group className="mb-3">
                            <Form.Label>País</Form.Label>
                            <SpecialSelect
                                options={countryOptions}
                                onChange={handleCountryChange}
                                selected={selectedCountry}
                                placeholder="Seleccionar país..."
                            />
                            <Form.Text className="text-muted">
                                Seleccione el país para filtrar proyectos.
                            </Form.Text>
                        </Form.Group>

                        {/* Proyecto */}
                        <Form.Group className="mb-4">
                            <Form.Label>Proyecto *</Form.Label>
                            <SpecialSelect
                                options={projectOptions}
                                onChange={handleProjectChange}
                                selected={selectedProject}
                                loading={loadingProjects}
                                loadingMessage="Cargando proyectos..."
                                placeholder="Buscar proyecto..."
                                noOptionsMessage={loadingProjects ? "Cargando..." : "No hay proyectos disponibles"}
                                setInvalid={hasError}
                            />
                            <Form.Text className="text-muted">
                                El proyecto debe estar configurado en el Master Sheet con al menos un link válido.
                            </Form.Text>
                        </Form.Group>

                        {/* Botones */}
                        <div className="d-flex justify-content-end gap-2">
                            <Link to="/admin/price-extractions">
                                <Button variant="outline-secondary" disabled={submitting}>
                                    Cancelar
                                </Button>
                            </Link>
                            <Button type="submit" variant="primary" disabled={submitting || !selectedProject}>
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Ejecutando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-play-fill me-1"></i>
                                        Ejecutar Extraccion
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Estado de procesamiento */}
                        {submitting && (
                            <Alert variant="warning" className="mt-4">
                                <strong>Procesando extraccion...</strong>
                                <p className="mb-0 mt-2">
                                    Descargando archivos del Sheet, ejecutando OCR y analizando precios con IA.
                                    Esto puede tomar entre 30 segundos y 2 minutos.
                                </p>
                                {showEscapeButton && (
                                    <>
                                        <hr />
                                        <p className="mb-2">
                                            Esta tardando mas de lo habitual. La extraccion puede seguir
                                            procesandose en segundo plano. Podes ir al listado para verla
                                            cuando termine.
                                        </p>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => navigate('/admin/price-extractions')}
                                        >
                                            <i className="bi bi-list-ul me-1"></i>
                                            Ir al listado de extracciones
                                        </Button>
                                    </>
                                )}
                            </Alert>
                        )}

                        {/* Resultado */}
                        {result && !submitting && (
                            <>
                                {result.success ? (
                                    <Alert variant="success" className="mt-4">
                                        <strong><i className="bi bi-check-circle me-2"></i>Extraccion completada!</strong>
                                        <p className="mb-2 mt-2">
                                            Proyecto: <strong>{result.projectName}</strong>
                                            {result.country && <span className="text-muted ms-2">({result.country})</span>}
                                        </p>
                                        <p className="mb-2">
                                            Unidades procesadas: <strong>{result.totalUnits}</strong>
                                        </p>
                                        <p className="mb-3">
                                            Cambios detectados: <strong>{result.changesPending}</strong>
                                        </p>
                                        <Button
                                            variant="success"
                                            onClick={() => navigate(`/admin/price-extractions/validate/${result.extractionId}`)}
                                        >
                                            <i className="bi bi-arrow-right me-1"></i>
                                            Ver y Validar Cambios
                                        </Button>
                                    </Alert>
                                ) : (
                                    <Alert variant="danger" className="mt-4">
                                        <strong><i className="bi bi-x-circle me-2"></i>Error en la extraccion</strong>
                                        <p className="mb-3 mt-2 small text-muted">
                                            Si el error fue por demora, la extraccion puede haberse creado igual.
                                            Revisa el listado.
                                        </p>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => navigate('/admin/price-extractions')}
                                        >
                                            <i className="bi bi-list-ul me-1"></i>
                                            Ir al listado de extracciones
                                        </Button>
                                    </Alert>
                                )}
                            </>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </div>
    )
}
